// src/utils/audioUtils.ts

export const base64ToBlob = (base64: string, contentType: string): Blob => {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
};

export const convertFloat32ToInt16 = (
    float32Array: Float32Array
): Int16Array => {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
        let s = Math.max(-1, Math.min(1, float32Array[i]));
        int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return int16Array;
};

export const getAudioDuration = (blob: Blob): Promise<number> => {
    return new Promise((resolve) => {
        // Create a FileReader to read the blob
        const reader = new FileReader();

        // Helper function to estimate duration from file size
        const estimateDurationFromSize = (blob: Blob): number => {
            // Assuming 128 kbps bitrate: 16 KB/second
            const estimatedDuration = blob.size / (16 * 1024);
            return Math.min(estimatedDuration, 600); // Cap at 10 minutes
        };

        reader.onload = function (event) {
            // Create an AudioContext
            const audioContext = new (window.AudioContext ||
                (window as any).webkitAudioContext)();

            // Set a timeout in case decoding hangs
            const timeoutId = setTimeout(() => {
                console.warn("Audio decoding timed out, using estimate");
                resolve(estimateDurationFromSize(blob));
            }, 3000);

            // Decode the audio data
            audioContext.decodeAudioData(
                event.target?.result as ArrayBuffer,
                (buffer) => {
                    clearTimeout(timeoutId);
                    const duration = buffer.duration;
                    console.log(
                        `Audio duration calculated: ${duration.toFixed(2)}s`
                    );
                    if (isFinite(duration)) {
                        resolve(duration);
                    } else {
                        resolve(estimateDurationFromSize(blob));
                    }
                    audioContext.close();
                },
                (error) => {
                    clearTimeout(timeoutId);
                    console.error("Error decoding audio data:", error);
                    console.log(
                        `Using estimated duration based on file size: ${estimateDurationFromSize(
                            blob
                        ).toFixed(2)}s`
                    );
                    resolve(estimateDurationFromSize(blob));
                    audioContext.close();
                }
            );
        };

        reader.onerror = function () {
            console.error("Error reading audio blob");
            resolve(estimateDurationFromSize(blob));
        };

        // Read the blob as an ArrayBuffer
        reader.readAsArrayBuffer(blob);
    });
};
